import { useState, useEffect, useRef } from 'react';
import { Download, XCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, IdTokenResult } from "firebase/auth";
import { useToast } from "@/hooks/use-toast";
import { HeaderSettings, INITIAL_HEADER_SETTINGS } from "./Headerfiles/types";
import { CustomHeader } from "./Headerfiles/CustomHeader";
import html2canvas from "html2canvas";
import ContentLoader from "../authentication/ContentLoader";

interface FeeCategory {
  name: string;
  term1: number;
  term2: number;
  term3: number;
  paidOnce?: boolean;
  daySchoolTerm1?: number;
  daySchoolTerm2?: number;
  daySchoolTerm3?: number;
}

interface LevelFees {
  level: string;
  categories: FeeCategory[];
  classes: string[];
  feeType: string;
  isGovernmentSponsored?: boolean;
  governmentSponsorshipAmount?: number;
  termIdsByClass?: Record<string, { term1: number; term2: number; term3: number }>;
}

interface DeployedFeeStructure {
  levelFees: LevelFees[];
  deployedAt: Date;
  showGovernmentAmount: boolean;
  insights?: string;
  importantNotes?: string;
  termIdsByClass?: Record<string, { term1: number; term2: number; term3: number }>;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

const DeployedFeeStructures: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [schoolDetails, setSchoolDetails] = useState<{name: string; address: string; email: string; logoUrl: string; motto?: string; type?: string; phone?: string} | null>(null);
  const [deployedStructure, setDeployedStructure] = useState<DeployedFeeStructure | null>(null);
  const [feeTypeView, setFeeTypeView] = useState<"boarding" | "day">("boarding");
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings>(INITIAL_HEADER_SETTINGS);
  const headerPreviewRef = useRef<HTMLDivElement>(null);

  // Fetch school ID from auth
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          let foundSchoolID: string | null = null;
          if (claimSchoolID) {
            foundSchoolID = claimSchoolID;
          } else if (defaultAffiliation?.schoolID) {
            foundSchoolID = defaultAffiliation.schoolID;
          } else {
            const adminAff = affiliations.find((a) => a.role === "admin" && a.schoolID);
            if (adminAff?.schoolID) {
              foundSchoolID = adminAff.schoolID;
            }
          }
          setSchoolID(foundSchoolID);
        } catch (error) {
          console.error("Error fetching school ID:", error);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch school details and header settings
  useEffect(() => {
    if (!schoolID) return;

    const fetchSchoolDetails = async () => {
      try {
        const schoolDoc = await getDoc(doc(db, `schools/${schoolID}/schoolDetails`, 'main'));
        if (schoolDoc.exists()) {
          const data = schoolDoc.data();
          setSchoolDetails({
            name: data.name || "",
            address: data.address || "",
            email: data.email || "",
            logoUrl: data.logoUrl || "",
            motto: data.motto || "",
            type: data.type || "",
            phone: data.phone || "",
          });
        }

        // Fetch header settings
        const headerDoc = await getDoc(doc(db, `schools/${schoolID}/headerSettings/default`));
        if (headerDoc.exists()) {
          const headerData = headerDoc.data() as HeaderSettings;
          // Merge with school details
          const schoolDocData = schoolDoc.exists() ? schoolDoc.data() : {};
          
          // Build combined address like HeaderDesigner does
          let combinedAddress = schoolDocData?.address || '';
          const phone = schoolDocData?.phone || '';
          const email = schoolDocData?.email || '';
          if (phone || email) {
            let contactLine = '';
            if (phone) contactLine += `Phone: ${phone}`;
            if (email) contactLine += (contactLine ? ' | ' : '') + `Email: ${email}`;
            combinedAddress = combinedAddress ? `${combinedAddress}\n${contactLine}` : contactLine;
          }

          setHeaderSettings({
            ...INITIAL_HEADER_SETTINGS,
            ...headerData,
            schoolName: { ...INITIAL_HEADER_SETTINGS.schoolName, ...(headerData.schoolName || {}), text: schoolDocData?.name || headerData.schoolName?.text || '' },
            motto: { ...INITIAL_HEADER_SETTINGS.motto, ...(headerData.motto || {}), text: schoolDocData?.motto || headerData.motto?.text || '' },
            address: { ...INITIAL_HEADER_SETTINGS.address, ...(headerData.address || {}), text: combinedAddress || headerData.address?.text || '' },
            logo: { ...INITIAL_HEADER_SETTINGS.logo, ...(headerData.logo || {}), url: schoolDocData?.logoUrl || headerData.logo?.url || null },
          });
        }
      } catch (error) {
        console.error("Error fetching school details:", error);
      }
    };

    fetchSchoolDetails();
  }, [schoolID]);

  // Fetch deployed structure
  useEffect(() => {
    if (!schoolID) return;

    const fetchDeployedStructure = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, `schools/${schoolID}/deployedFeeStructures`, 'current');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setDeployedStructure({
            levelFees: data.levelFees || [],
            deployedAt: data.deployedAt?.toDate() || new Date(),
            showGovernmentAmount: data.showGovernmentAmount || false,
            insights: data.insights,
            importantNotes: data.importantNotes,
            termIdsByClass: data.termIdsByClass || {},
          });
        } else {
          setDeployedStructure(null);
        }
      } catch (error) {
        console.error("Error fetching deployed structure:", error);
        toast({
          title: "Error",
          description: "Failed to load deployed fee structure",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDeployedStructure();
  }, [schoolID, toast]);



  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

    const calculateTermTotal = (
    categories: FeeCategory[], 
    term: "term1" | "term2" | "term3",
    feeType: "boarding" | "day" = "boarding"
  ) => {
    if (feeType === "day") {
      const dayTermMap = { term1: "daySchoolTerm1", term2: "daySchoolTerm2", term3: "daySchoolTerm3" };
      return categories.reduce((sum, c) => sum + (c[dayTermMap[term] as keyof FeeCategory] as number || 0), 0);
    }
    return categories.reduce((sum, c) => sum + c[term], 0);
  };

  const generatePDF = async () => {
    if (!deployedStructure) return;

    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
      });

    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      let jsPDF = (window as any).jsPDF;
      if (!jsPDF && (window as any).jspdf) {
        jsPDF = (window as any).jspdf.jsPDF;
      }
      
      if (!jsPDF) {
        throw new Error("jsPDF library not loaded");
      }
      
      const pdfDoc = new jsPDF();
      const pageWidth = pdfDoc.internal.pageSize.width;
      const horizontalMargin = 10;
      const headerWidthMm = pageWidth - horizontalMargin * 2;
      let headerImageData: string | null = null;
      let headerImageHeightMm = 0;

      if (headerPreviewRef.current && headerSettings.isEnabled) {
        try {
          const html2canvasOptions: any = {
            scale: Math.min(window.devicePixelRatio || 2, 3),
            useCORS: true,
            backgroundColor: '#ffffff'
          };
          const canvas = await html2canvas(headerPreviewRef.current, html2canvasOptions);
          headerImageData = canvas.toDataURL('image/png');
          const aspectRatio = canvas.height / canvas.width;
          headerImageHeightMm = headerWidthMm * aspectRatio;
        } catch (error) {
          console.error("Failed to capture header preview:", error);
        }
      }

      const addHeader = async () => {
        if (headerImageData) {
          const startY = 10;
          pdfDoc.addImage(headerImageData, 'PNG', horizontalMargin, startY, headerWidthMm, headerImageHeightMm);
          let currentY = startY + headerImageHeightMm + 4;
          pdfDoc.setDrawColor(200, 200, 200);
          pdfDoc.line(horizontalMargin, currentY, pageWidth - horizontalMargin, currentY);
          currentY += 5;
          pdfDoc.setFontSize(14);
          pdfDoc.setFont('helvetica', 'bold');
          pdfDoc.setTextColor(0, 0, 0);
          pdfDoc.text("Fee Structure - Academic Year 2024/2025", pageWidth / 2, currentY, { align: 'center' });
          return currentY + 6;
        }

        const s = headerSettings;
        let currentY = 10;
        if (schoolDetails?.logoUrl) {
          try {
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
              const image = new Image();
              image.crossOrigin = "anonymous";
              image.onload = () => resolve(image);
              image.onerror = reject;
              image.src = schoolDetails.logoUrl;
            });
            const logoSize = 25;
            pdfDoc.addImage(img, 'PNG', horizontalMargin, currentY, logoSize, logoSize);
          } catch (error) {
            console.error("Failed to load logo:", error);
          }
        }
        pdfDoc.setFontSize(18);
        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.setTextColor(37, 99, 235);
        pdfDoc.text(schoolDetails?.name || s.schoolName.text || "School Name", pageWidth / 2, currentY + 8, { align: "center" });
        currentY += 14;
        pdfDoc.setFontSize(10);
        pdfDoc.setFont("helvetica", "normal");
        pdfDoc.setTextColor(80, 80, 80);
        if (schoolDetails?.address || s.address.text) {
          pdfDoc.text(schoolDetails?.address || s.address.text, pageWidth / 2, currentY, { align: "center" });
          currentY += 5;
        }
        if (schoolDetails?.motto || s.motto.text) {
          pdfDoc.setFont("helvetica", "italic");
          pdfDoc.setFontSize(9);
          pdfDoc.text(schoolDetails?.motto || s.motto.text, pageWidth / 2, currentY, { align: "center" });
          currentY += 5;
        }
        currentY += 3;
        pdfDoc.setDrawColor(200, 200, 200);
        pdfDoc.line(horizontalMargin, currentY, pageWidth - horizontalMargin, currentY);
        currentY += 5;
        pdfDoc.setFontSize(14);
        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.setTextColor(0, 0, 0);
        pdfDoc.text("Fee Structure - Academic Year 2024/2025", pageWidth / 2, currentY, { align: "center" });
        
        return currentY + 6;
      };
      
      const initialHeaderHeight = await addHeader();
      let y = initialHeaderHeight + 5;

      for (let i = 0; i < deployedStructure.levelFees.length; i++) {
        const lvl = deployedStructure.levelFees[i];
        if (i > 0) {
          pdfDoc.addPage();
          await addHeader();
          y = 40;
        }

        const levelText = `${lvl.level} Level`;
        (pdfDoc as any).autoTable({
          startY: y,
          body: [[levelText]],
          theme: 'plain',
          styles: {
            fontStyle: 'bold',
            fontSize: 14,
            fillColor: [248, 250, 252],
            textColor: [37, 99, 235],
            cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
          },
          margin: { left: 10, right: 10 },
        });
        y = (pdfDoc as any).lastAutoTable.finalY + 6;

        const formatPDFCurrency = (amount: number) => {
          if (amount === 0) return "-";
          return "KSh " + amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        };

        const body = lvl.categories.map((c) => [
          c.paidOnce ? `${c.name} *` : c.name,
          formatPDFCurrency(c.term1),
          formatPDFCurrency(c.term2),
          formatPDFCurrency(c.term3),
        ]);

        const t1 = calculateTermTotal(lvl.categories, "term1");
        const t2 = calculateTermTotal(lvl.categories, "term2");
        const t3 = calculateTermTotal(lvl.categories, "term3");
        const annualTotal = t1 + t2 + t3;
        
        body.push(
          ["TERM TOTAL", formatPDFCurrency(t1), formatPDFCurrency(t2), formatPDFCurrency(t3)],
          ["ANNUAL TOTAL", formatPDFCurrency(annualTotal), "-", "-"]
        );

        if (deployedStructure.showGovernmentAmount && lvl.isGovernmentSponsored && lvl.governmentSponsorshipAmount && lvl.governmentSponsorshipAmount > 0) {
          body.push(
            ["GOVERNMENT SPONSORSHIP", formatPDFCurrency(lvl.governmentSponsorshipAmount), "-", "-"],
            ["NET AMOUNT PAYABLE", formatPDFCurrency(Math.max(0, annualTotal - lvl.governmentSponsorshipAmount)), "-", "-"]
          );
        } else if (lvl.isGovernmentSponsored && lvl.governmentSponsorshipAmount && lvl.governmentSponsorshipAmount > 0) {
          body.push(
            ["AMOUNT PAYABLE", formatPDFCurrency(Math.max(0, annualTotal - lvl.governmentSponsorshipAmount)), "-", "-"]
          );
        }

        (pdfDoc as any).autoTable({
          startY: y,
          head: [["Fee Category", "Term 1", "Term 2", "Term 3"]],
          body,
          theme: "striped",
          headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
          styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak', cellWidth: 'wrap' },
          columnStyles: {
            0: { cellWidth: 'auto', halign: "left", overflow: 'linebreak' },
            1: { cellWidth: 40, halign: "center" },
            2: { cellWidth: 40, halign: "center" },
            3: { cellWidth: 40, halign: "center" },
          },
          margin: { left: 14, right: 14 },
        });
        y = (pdfDoc as any).lastAutoTable.finalY + 10;

        if (lvl.feeType === "uniform") {
          pdfDoc.setFontSize(9);
          pdfDoc.setTextColor(37, 99, 235);
          pdfDoc.setFont("helvetica", "italic");
          pdfDoc.text("Note: This fee structure applies uniformly to all students across all classes and streams.", 14, y);
          y += 10;
        }
      }

      if (deployedStructure.insights || deployedStructure.importantNotes) {
        pdfDoc.addPage();
        await addHeader();
        let notesY = 45;

        if (deployedStructure.insights) {
          // Fee Structure Insights
          pdfDoc.setFillColor(248, 250, 252);
          pdfDoc.rect(14, notesY - 2, pageWidth - 28, 10, "F");
          pdfDoc.setTextColor(37, 99, 235);
          pdfDoc.setFontSize(13);
          pdfDoc.setFont("helvetica", "bold");
          pdfDoc.text("Fee Structure Insights", 18, notesY + 5);
          notesY += 15;

          pdfDoc.setFontSize(9);
          pdfDoc.setTextColor(60, 60, 60);
          pdfDoc.setFont("helvetica", "normal");

          const insightLines = deployedStructure.insights.split('\n').map(line => line.trim()).filter(line => line);
          
          insightLines.forEach(line => {
            const splitLines = pdfDoc.splitTextToSize(line, pageWidth - 36); // 18 margin on each side
            pdfDoc.text(splitLines, 18, notesY);
            notesY += (splitLines.length * 5);
          });

          notesY += 10;
        }

        if (deployedStructure.importantNotes) {
          // Important Notes
          pdfDoc.setFillColor(248, 250, 252);
          pdfDoc.rect(14, notesY - 2, pageWidth - 28, 10, "F");
          pdfDoc.setTextColor(37, 99, 235);
          pdfDoc.setFontSize(13);
          pdfDoc.setFont("helvetica", "bold");
          pdfDoc.text("Important Notes", 18, notesY + 5);
          notesY += 15;

          pdfDoc.setFontSize(9);
          pdfDoc.setTextColor(60, 60, 60);
          pdfDoc.setFont("helvetica", "normal");

          const noteLines = deployedStructure.importantNotes.split('\n').map(line => line.trim()).filter(line => line);
          
          noteLines.forEach(line => {
            const splitLines = pdfDoc.splitTextToSize(line, pageWidth - 36); // 18 margin on each side
            pdfDoc.text(splitLines, 18, notesY);
            notesY += (splitLines.length * 5);
          });
        }
      }

      pdfDoc.save("Deployed-Fee-Structure.pdf");
      toast({
        title: "Success",
        description: "PDF downloaded successfully",
      });
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <ContentLoader isLoading={loading} message="">
        {!deployedStructure ? (
          <div className="max-w-7xl mx-auto space-y-6">
            <Card className="m-2">
              <CardContent className="p-12 text-center">
                <XCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Deployed Fee Structure</h3>
                <p className="text-gray-600">No fee structure has been deployed yet.</p>
              </CardContent>
            </Card>
          </div>
        ) : (
      <div className="max-w-7xl mx-auto space-y-6 p-4">
        <Card className="m-2 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-100 to-blue-50 dark:from-blue-900/40 dark:to-blue-900/20 border-b-2 border-blue-300 dark:border-blue-700">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl font-bold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6" />
                  Currently Deployed Fee Structure
                </CardTitle>
                <CardDescription className="mt-1 text-blue-700 dark:text-blue-300">
                  Deployed on {deployedStructure.deployedAt.toLocaleDateString()} at {deployedStructure.deployedAt.toLocaleTimeString()}
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={deployedStructure.showGovernmentAmount ? "default" : "secondary"} className="text-xs px-3 py-1">
                  {deployedStructure.showGovernmentAmount ? "Govt Amount Visible" : "Govt Amount Hidden"}
                </Badge>
                <Button
                  onClick={generatePDF}
                  size="sm"
                  className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Fee Type Toggle - Only show if school is Mixed */}
        {schoolDetails?.type === "Mixed (Day & Boarding)" && (
          <div className="m-2">
            <Tabs value={feeTypeView} onValueChange={(value) => setFeeTypeView(value as "boarding" | "day")}>
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
                <TabsTrigger 
                  value="boarding"
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 dark:text-gray-300 font-semibold"
                >
                  Boarding School Fees
                </TabsTrigger>
                <TabsTrigger 
                  value="day"
                  className="data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 dark:text-gray-300 font-semibold"
                >
                  Day School Fees
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {deployedStructure.levelFees.map((level) => {
          // Check if fee data exists for the selected view
          const hasDaySchoolFees = level.categories.some(cat => 
            cat.daySchoolTerm1 !== undefined || cat.daySchoolTerm2 !== undefined || cat.daySchoolTerm3 !== undefined
          );
          const hasBoardingFees = level.categories.some(cat => 
            cat.term1 !== undefined && cat.term1 !== null
          );
          
          const showNoDataMessage = schoolDetails?.type === "Mixed (Day & Boarding)" && (
            (feeTypeView === "day" && !hasDaySchoolFees) ||
            (feeTypeView === "boarding" && !hasBoardingFees)
          );
          
          const t1 = calculateTermTotal(level.categories, "term1", feeTypeView);
          const t2 = calculateTermTotal(level.categories, "term2", feeTypeView);
          const t3 = calculateTermTotal(level.categories, "term3", feeTypeView);
          const annualTotal = t1 + t2 + t3;
          const netPayable = level.isGovernmentSponsored && level.governmentSponsorshipAmount 
            ? Math.max(0, annualTotal - level.governmentSponsorshipAmount)
            : annualTotal;

          return (
            <Card key={level.level} className="m-2 border-2 border-gray-300 dark:border-gray-600 shadow-md bg-white dark:bg-gray-800">
              <CardHeader className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{level.level}</CardTitle>
                  {schoolDetails?.type === "Mixed (Day & Boarding)" && (
                    <Badge 
                      variant="outline" 
                      className={`text-sm font-semibold ${
                        feeTypeView === "boarding" 
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" 
                          : "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                      }`}
                    >
                      {feeTypeView === "boarding" ? "Boarding Fees" : "Day School Fees"}
                    </Badge>
                  )}
                </div>
                {level.feeType === "uniform" && (
                  <Badge variant="outline" className="w-fit mt-2 border-blue-500 text-blue-700 dark:text-blue-300">
                    Uniform Fee - Applies to All Classes
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="p-6 bg-white dark:bg-gray-800">
                {showNoDataMessage ? (
                  <div className="p-8 text-center bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
                    <div className="flex flex-col items-center gap-4">
                      <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-4">
                        <XCircle className="h-12 w-12 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-amber-900 dark:text-amber-200 mb-2">
                          {feeTypeView === "day" ? "Day School Fees" : "Boarding School Fees"} Not Recorded
                        </h3>
                        <p className="text-amber-800 dark:text-amber-300">
                          Fee structure for {feeTypeView === "day" ? "day school students" : "boarding school students"} has not been recorded for {level.level}.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className="overflow-x-auto border-2 border-gray-200 dark:border-gray-700 rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
                        <TableHead className="font-bold text-gray-900 dark:text-white">Fee Category</TableHead>
                        <TableHead className="text-right font-bold text-gray-900 dark:text-white">Term 1</TableHead>
                        <TableHead className="text-right font-bold text-gray-900 dark:text-white">Term 2</TableHead>
                        <TableHead className="text-right font-bold text-gray-900 dark:text-white">Term 3</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {level.categories.map((cat, idx) => {
                        const displayTerm1 = feeTypeView === "day" ? (cat.daySchoolTerm1 ?? cat.term1) : cat.term1;
                        const displayTerm2 = feeTypeView === "day" ? (cat.daySchoolTerm2 ?? cat.term2) : cat.term2;
                        const displayTerm3 = feeTypeView === "day" ? (cat.daySchoolTerm3 ?? cat.term3) : cat.term3;
                        
                        return (
                        <TableRow key={idx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <TableCell className="font-medium text-gray-900 dark:text-white">
                            {cat.name}
                            {cat.paidOnce && <Badge variant="outline" className="ml-2 text-xs border-purple-500 text-purple-700 dark:text-purple-300">Paid Once</Badge>}
                          </TableCell>
                          <TableCell className="text-right text-gray-700 dark:text-gray-300">{formatCurrency(displayTerm1)}</TableCell>
                          <TableCell className="text-right text-gray-700 dark:text-gray-300">{formatCurrency(displayTerm2)}</TableCell>
                          <TableCell className="text-right text-gray-700 dark:text-gray-300">{formatCurrency(displayTerm3)}</TableCell>
                        </TableRow>
                        );
                      })}
                      <TableRow className="font-semibold bg-gray-200 dark:bg-gray-700 border-y-2 border-gray-300 dark:border-gray-600">
                        <TableCell className="text-gray-900 dark:text-white">TERM TOTAL</TableCell>
                        <TableCell className="text-right text-gray-900 dark:text-white">{formatCurrency(t1)}</TableCell>
                        <TableCell className="text-right text-gray-900 dark:text-white">{formatCurrency(t2)}</TableCell>
                        <TableCell className="text-right text-gray-900 dark:text-white">{formatCurrency(t3)}</TableCell>
                      </TableRow>
                      <TableRow className="font-bold bg-gradient-to-r from-blue-100 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/20 border-y-2 border-blue-300 dark:border-blue-700">
                        <TableCell className="text-blue-900 dark:text-blue-100">ANNUAL TOTAL</TableCell>
                        <TableCell className="text-right text-blue-900 dark:text-blue-100">{formatCurrency(annualTotal)}</TableCell>
                        <TableCell className="text-right text-blue-900 dark:text-blue-100">-</TableCell>
                        <TableCell className="text-right text-blue-900 dark:text-blue-100">-</TableCell>
                      </TableRow>
                      {deployedStructure.showGovernmentAmount && level.isGovernmentSponsored && level.governmentSponsorshipAmount && level.governmentSponsorshipAmount > 0 && (
                        <>
                          <TableRow className="bg-gradient-to-r from-green-100 to-green-50 dark:from-green-900/30 dark:to-green-900/20 border-y border-green-300 dark:border-green-700">
                            <TableCell className="font-semibold text-green-800 dark:text-green-300">GOVERNMENT SPONSORSHIP</TableCell>
                            <TableCell className="text-right font-semibold text-green-800 dark:text-green-300">
                              -{formatCurrency(level.governmentSponsorshipAmount)}
                            </TableCell>
                            <TableCell className="text-right text-green-800 dark:text-green-300">-</TableCell>
                            <TableCell className="text-right text-green-800 dark:text-green-300">-</TableCell>
                          </TableRow>
                          <TableRow className="font-bold bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-900/20 border-y-2 border-purple-300 dark:border-purple-700">
                            <TableCell className="text-purple-900 dark:text-purple-100">NET AMOUNT PAYABLE (After Govt Sponsorship)</TableCell>
                            <TableCell className="text-right text-purple-900 dark:text-purple-100">
                              {formatCurrency(netPayable)}
                            </TableCell>
                            <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                            <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                          </TableRow>
                        </>
                      )}
                      {!deployedStructure.showGovernmentAmount && level.isGovernmentSponsored && level.governmentSponsorshipAmount && level.governmentSponsorshipAmount > 0 && (
                        <TableRow className="font-bold bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-900/20 border-y-2 border-purple-300 dark:border-purple-700">
                          <TableCell className="text-purple-900 dark:text-purple-100">AMOUNT PAYABLE</TableCell>
                          <TableCell className="text-right text-purple-900 dark:text-purple-100">
                            {formatCurrency(netPayable)}
                          </TableCell>
                          <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                          <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {deployedStructure.insights && (
          <Card className="m-2 border-2 border-indigo-300 dark:border-indigo-700 shadow-md">
            <CardHeader className="bg-gradient-to-r from-indigo-100 to-indigo-50 dark:from-indigo-900/40 dark:to-indigo-900/20 border-b-2 border-indigo-300 dark:border-indigo-700">
              <CardTitle className="text-xl font-bold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
                Fee Structure Insights
                <Badge variant="secondary" className="bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 border-indigo-400 dark:border-indigo-600">Analysis</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 bg-white dark:bg-gray-800">
              <div className="space-y-3">
                {deployedStructure.insights.split('\n').map((line, index) => (
                  line.trim() && (
                    <div key={index} className="flex items-start gap-3 p-4 m-2 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg border-l-4 border-indigo-500 shadow-sm">
                      <div className="w-2 h-2 rounded-full bg-indigo-600 dark:bg-indigo-400 mt-2 flex-shrink-0"></div>
                      <p className="text-sm text-gray-800 dark:text-gray-200">{line}</p>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {deployedStructure.importantNotes && (
          <Card className="m-2 border-2 border-amber-300 dark:border-amber-700 shadow-md">
            <CardHeader className="bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-900/40 dark:to-amber-900/20 border-b-2 border-amber-300 dark:border-amber-700">
              <CardTitle className="text-xl font-bold text-amber-900 dark:text-amber-100">Important Notes</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 bg-white dark:bg-gray-800">
              <ul className="space-y-3">
                {deployedStructure.importantNotes.split('\n').map((line, index) => (
                  line.trim() && (
                    <li key={index} className="flex items-start gap-3 p-3 m-2 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg border-l-4 border-amber-500 shadow-sm">
                      <span className="text-amber-600 dark:text-amber-400 font-bold">•</span>
                      <span className="text-sm text-gray-800 dark:text-gray-200">{line}</span>
                    </li>
                  )
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
      )}
      </ContentLoader>
      <div
        ref={headerPreviewRef}
        style={{
          position: 'fixed',
          top: '-10000px',
          left: '-10000px',
          width: '794px',
          opacity: 0,
          pointerEvents: 'none',
          background: '#ffffff',
          zIndex: -1
        }}
        aria-hidden="true"
      >
        <CustomHeader settings={headerSettings} />
      </div>
    </>
  );
};

export default DeployedFeeStructures;
